<script setup>
import { computed, onMounted, ref } from 'vue';
import { getUserInvoices } from '../../utils/api';
import { formatDate } from '../../utils/formatDate';
import isMobile from '../../utils/isMobile';
import smoothScroll from '../../utils/smoothScroll';
import { default as t } from '../../utils/translationStrings';

// Data.
const items = ref([]);
const currentPage = ref(0);
const itemsPerPage = 12;
const header = ref(null);
const onMobile = ref(isMobile());

// Props.
defineProps({
  shopUrl: {
    type: String,
    required: true,
  },
});

// Computed.
const totalPageCount = computed(() => {
  return Math.ceil(items.value.length / itemsPerPage);
});
const currentPageItems = computed(() => {
  return items.value.slice(
    currentPage.value * itemsPerPage,
    currentPage.value * itemsPerPage + itemsPerPage,
  );
});

// Mounted.
onMounted(async () => {
  // Fetch all user documents.
  try {
    const response = await getUserInvoices();
    items.value = response?.data?.documents ?? [];
  } catch (e) {
    console.error(e);
  } finally {
    // Add resize event listener.
    window.addEventListener('resize', () => {
      onMobile.value = isMobile();
    });
  }
});

// Methods.
const setPage = (page) => {
  currentPage.value = page;

  // Scroll to the header section - offset.
  const headerOffset = header.value?.offsetTop || 0;
  smoothScroll(headerOffset - 220);
};
</script>

<template>
  <div class="invoices-list">
    <!-- Mimics a table. -->
    <!-- Don't show header on < L. -->
    <div
      v-if="!onMobile && items.length"
      class="invoices-list__header"
      ref="header">
      <div
        v-for="key in Object.keys(t.dashboardPayment.invoicesListHeader)"
        class="invoices-list__label"
        :key="key">
        {{ t.dashboardPayment.invoicesListHeader[key] }}
      </div>
    </div>

    <!-- Body/content. -->
    <transition-group name="page-change" tag="div" v-if="items.length">
      <div class="invoices-list__body" :key="currentPage">
        <div
          v-for="invoice in currentPageItems"
          class="invoices-list__item"
          :key="invoice.id">
          <div class="invoice">
            <div class="invoice__date">
              {{ formatDate(invoice.createdAt) }}
            </div>
            <div class="invoice__name">
              {{ invoice?.documentMediaFile?.fileName }}
            </div>
            <div class="invoice__total">
              {{ invoice?.extensions?.additionalData?.price }} €
            </div>

            <!-- Download link. -->
            <a
              :href="`${shopUrl}/account/order/document/${invoice.id}/${invoice.deepLinkCode}`"
              target="_blank"
              download
              class="invoice__download"
              :title="t.dashboard.downloadInvoice">
              <span class="visually-hidden">
                {{ t.dashboard.downloadInvoice }}
              </span>
              <i class="fa-regular fa-file-pdf" aria-hidden="true"></i>
            </a>
          </div>
        </div>
      </div>
    </transition-group>

    <!-- No results text. -->
    <p v-else class="invoices-list__empty">
      {{ t.dashboardPayment.invoicesListEmpty }}
    </p>

    <div class="pagination" v-if="items.length">
      <button
        :class="{
          'pagination-button--disabled': currentPage === 0,
        }"
        class="pagination-button"
        :title="t.previous"
        @click="setPage(currentPage - 1)">
        <i class="fa-regular fa-chevron-left"></i>
      </button>

      <div class="pagination__pages">
        <a
          v-for="(number, key) in totalPageCount"
          class="pagination__page"
          :class="{
            'pagination__page--active': currentPage === key,
          }"
          :key="key"
          @click.prevent="setPage(key)">
          {{ number }}
        </a>
      </div>
      <button
        :class="{
          'pagination-button--disabled': currentPage >= totalPageCount - 1,
        }"
        class="pagination-button"
        :title="t.next"
        @click="setPage(currentPage + 1)">
        <i class="fa-regular fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>
